/**
 * Sprint T-15 · Founder Security Center · Rate Limiting tab (read-only).
 *
 * Consume ONLY /api/superadmin/audit-events filtered by action=ratelimit.triggered.
 * No mutation, no configuration: la configuración runtime no existe aún
 * (env vars · ver T-14 final report). Cuando exista se ampliará esta vista.
 */
import React, { useMemo, useState } from "react";
import { Gauge, RefreshCw, ChevronLeft, ChevronRight, Activity, AlertOctagon } from "lucide-react";
import { WidgetCard, WidgetGrid, MetricCard, EmptyState, LoadingState, CitationBadge } from "../advisor";
import { useAuditEvents } from "../../hooks/useSecurity";

const PAGE_SIZE = 25;

const fmtIso = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "medium" });
  } catch {
    return iso;
  }
};

export default function RateLimitsTab() {
  const [page, setPage] = useState(0);
  const query = useMemo(() => ({
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    action: "ratelimit.triggered",
  }), [page]);

  const events = useAuditEvents(query);

  const data = events.data;
  const items = data?.items || [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Aggregate top IPs and top routes from this page (presentation-only)
  const aggregates = useMemo(() => {
    const ipMap = new Map();
    const routeMap = new Map();
    items.forEach((e) => {
      if (e.ip) ipMap.set(e.ip, (ipMap.get(e.ip) || 0) + 1);
      const route = e?.metadata?.route || e?.metadata?.bucket || "—";
      routeMap.set(route, (routeMap.get(route) || 0) + 1);
    });
    const ip = [...ipMap.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);
    const route = [...routeMap.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);
    return { ip, route };
  }, [items]);

  return (
    <section className="space-y-4" data-testid="security-ratelimit-tab">
      <WidgetCard
        title="Rate Limiting · diagnóstico"
        description="Vista read-only de los disparos de rate-limit. La configuración runtime (límites por bucket) se ajusta vía variables de entorno · cuando exista un endpoint de configuración dinámica se ampliará este panel."
        citations={["audit.ratelimit.triggered", "security.rate_limiter (in-memory · T-14)"]}
        testId="widget-ratelimit-intro"
      >
        <div className="flex items-center gap-2 text-xs text-[var(--ag-text-dim)]" data-testid="ratelimit-intro-block">
          <Gauge className="h-4 w-4" />
          Buckets default · login 5/min · ai 30/min · admin 60/min · founder 120/min · client 60/min · public 60/min · apikey 600/min.
        </div>
      </WidgetCard>

      <WidgetGrid cols="lg:grid-cols-3">
        <WidgetCard title="Disparos totales" citations={["audit.ratelimit.triggered"]} testId="widget-ratelimit-total">
          {events.isLoading && <LoadingState />}
          {events.error && <EmptyState message="No se pudo cargar." />}
          {data && (
            <MetricCard
              label="ratelimit.triggered"
              value={total}
              hint="Retención backend 365 días"
              testId="ratelimit-total"
            />
          )}
        </WidgetCard>

        <WidgetCard title="Top IPs (página actual)" citations={["client-side aggregation"]} testId="widget-ratelimit-top-ips">
          {events.isLoading && <LoadingState />}
          {data && aggregates.ip.length === 0 && <EmptyState testId="ratelimit-top-ips-empty" />}
          {data && aggregates.ip.length > 0 && (
            <ul className="space-y-1" data-testid="ratelimit-top-ips-list">
              {aggregates.ip.map(([ip, count]) => (
                <li key={ip} className="flex items-center justify-between rounded border border-[var(--ag-border)] px-2 py-1 text-xs">
                  <span className="font-mono">{ip}</span>
                  <span className="text-[var(--ag-text-dim)]">{count}</span>
                </li>
              ))}
            </ul>
          )}
        </WidgetCard>

        <WidgetCard title="Top rutas (página actual)" citations={["metadata.route|bucket"]} testId="widget-ratelimit-top-routes">
          {events.isLoading && <LoadingState />}
          {data && aggregates.route.length === 0 && <EmptyState testId="ratelimit-top-routes-empty" />}
          {data && aggregates.route.length > 0 && (
            <ul className="space-y-1" data-testid="ratelimit-top-routes-list">
              {aggregates.route.map(([route, count]) => (
                <li key={route} className="flex items-center justify-between rounded border border-[var(--ag-border)] px-2 py-1 text-xs">
                  <span className="font-mono truncate max-w-[60%]" title={route}>{route}</span>
                  <span className="text-[var(--ag-text-dim)]">{count}</span>
                </li>
              ))}
            </ul>
          )}
        </WidgetCard>
      </WidgetGrid>

      <WidgetCard
        title={`Eventos · ${total} disparos registrados`}
        description="Listado paginado · ordenado por timestamp desc."
        citations={["/api/superadmin/audit-events?action=ratelimit.triggered"]}
        testId="widget-ratelimit-events"
        actions={
          <button
            data-testid="ratelimit-refresh-btn"
            type="button"
            onClick={() => events.refetch()}
            className="inline-flex items-center gap-1 rounded border border-[var(--ag-border)] px-2 py-1 text-[10px] hover:bg-[var(--ag-surface-2)]"
            aria-label="Refrescar"
          >
            <RefreshCw className={`h-3 w-3 ${events.isFetching ? "animate-spin" : ""}`} />
          </button>
        }
      >
        {events.isLoading && <LoadingState />}
        {events.error && <EmptyState message="No se pudo cargar." />}
        {data && items.length === 0 && (
          <div className="rounded border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs text-emerald-500" data-testid="ratelimit-empty">
            <Activity className="mr-1 inline h-3 w-3" />
            Sin disparos de rate-limit registrados. El sistema está sano.
          </div>
        )}
        {data && items.length > 0 && (
          <>
            <div className="-mx-1 overflow-x-auto">
              <table className="min-w-full text-xs" data-testid="ratelimit-table">
                <thead className="border-b border-[var(--ag-border)] text-[10px] uppercase tracking-wide text-[var(--ag-text-dim)]">
                  <tr>
                    <th className="px-2 py-2 text-left">Hora</th>
                    <th className="px-2 py-2 text-left">IP</th>
                    <th className="px-2 py-2 text-left">Actor</th>
                    <th className="px-2 py-2 text-left">Bucket / ruta</th>
                    <th className="px-2 py-2 text-left">Metadata</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((e, i) => (
                    <tr
                      key={`${e.ts_iso}-${i}`}
                      data-testid={`ratelimit-row-${i}`}
                      className="border-b border-[var(--ag-border)]/60"
                    >
                      <td className="px-2 py-1.5 align-top font-mono text-[10px] text-[var(--ag-text-dim)]">
                        {fmtIso(e.ts_iso)}
                      </td>
                      <td className="px-2 py-1.5 align-top font-mono text-[10px]">
                        {e.ip || "—"}
                      </td>
                      <td className="px-2 py-1.5 align-top">
                        <span className="truncate max-w-[160px] inline-block" title={e.actor_email}>
                          {e.actor_email || "anon"}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 align-top font-mono text-[10px]">
                        {e?.metadata?.bucket || e?.metadata?.route || "—"}
                      </td>
                      <td className="px-2 py-1.5 align-top text-[10px] text-[var(--ag-text-dim)]">
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(e?.metadata || {}).slice(0, 4).map(([k, v]) => (
                            <span key={k} className="rounded bg-[var(--ag-surface-2)] px-1.5 py-0.5 font-mono text-[9px]">
                              {k}: {String(v).slice(0, 24)}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-3 flex items-center justify-between gap-3 text-xs" data-testid="ratelimit-pagination">
              <div className="text-[var(--ag-text-dim)]">
                Mostrando {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} de {total}
              </div>
              <div className="flex items-center gap-1">
                <button
                  data-testid="ratelimit-page-prev"
                  type="button"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0 || events.isFetching}
                  className="inline-flex items-center gap-1 rounded border border-[var(--ag-border)] px-2 py-0.5 text-[10px] hover:bg-[var(--ag-surface-2)] disabled:opacity-30"
                >
                  <ChevronLeft className="h-3 w-3" /> Anterior
                </button>
                <span className="px-2 text-[10px] tabular-nums">pág {page + 1} / {totalPages}</span>
                <button
                  data-testid="ratelimit-page-next"
                  type="button"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={(page + 1) * PAGE_SIZE >= total || events.isFetching}
                  className="inline-flex items-center gap-1 rounded border border-[var(--ag-border)] px-2 py-0.5 text-[10px] hover:bg-[var(--ag-surface-2)] disabled:opacity-30"
                >
                  Siguiente <ChevronRight className="h-3 w-3" />
                </button>
              </div>
            </div>
          </>
        )}
      </WidgetCard>

      <div className="rounded border border-[var(--ag-border)] bg-[var(--ag-surface-2)]/40 p-3 text-[11px] text-[var(--ag-text-dim)]" data-testid="ratelimit-config-hint">
        <AlertOctagon className="mr-1 inline h-3 w-3" />
        Configuración runtime: <code className="font-mono">AG_RATELIMIT_*_LIMIT</code> · sin endpoint de admin todavía.
        Cuando exista un endpoint de configuración dinámica esta vista se ampliará para mostrar y editar los límites.
      </div>

      <footer className="border-t border-[var(--ag-border)] pt-3">
        <CitationBadge sources={["T-14 in-memory ratelimit", "365d retention", "ai_platform_freeze=true"]} />
      </footer>
    </section>
  );
}
